import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navigation from './common/Navigation';
import DeleteSalonModal from './salon/DeleteSalonModal';
import './Dashboard.css';

const Dashboard = () => {
  const { t } = useLanguage();
  const [salons, setSalons] = useState([]);
  const [loadingSalons, setLoadingSalons] = useState(true);
  const [salonError, setSalonError] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [salonToDelete, setSalonToDelete] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const navigate = useNavigate();

  const handleCreateSalon = () => {
    // Check subscription before allowing salon creation
    if (!subscription || (!subscription.subscription && !subscription.isOnTrial)) {
      // No subscription and not on trial
      navigate('/pricing');
      return;
    }
    
    if (subscription.subscription?.isExpired) {
      // Subscription expired
      navigate('/pricing');
      return;
    }
    
    navigate('/create-salon');
  };

  const handleDeleteSalon = (salon) => {
    setSalonToDelete(salon);
    setDeleteModalOpen(true);
  };

  const handleDeleteSuccess = () => {
    fetchSalons(); // Refresh the salon list
  };

  const fetchSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/subscriptions/current', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const fetchSalons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/salons', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSalons(response.data.salons);
      setSalonError('');
    } catch (error) {
      if (error.response?.data?.error) {
        setSalonError(error.response.data.error);
      } else {
        setSalonError('Failed to fetch salons');
      }
    } finally {
      setLoadingSalons(false);
    }
  };

  useEffect(() => {
    fetchSubscription();
    fetchSalons();
  }, []);

  // Refresh salons when component comes into focus (e.g., after creating a salon)
  useEffect(() => {
    const handleFocus = () => {
      fetchSalons();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);


  return (
    <div className="dashboard-container">
      <Navigation />

      <main className="dashboard-main">
        <div className="welcome-section">
          <p>{t('dashboard.welcomeTo')} {t('dashboard.description')}</p>
        </div>

        {/* Subscription Status Banner */}
        {!loadingSubscription && subscription && (
          <>
            {subscription.isOnTrial && subscription.subscription?.trialEndsAt && (
              <div className="subscription-banner trial">
                <div className="banner-icon">🎁</div>
                <div className="banner-content">
                  <h4>{t('subscription.freeTrialActive')}</h4>
                  <p>
                    {t('subscription.trialEndsOn')} {new Date(subscription.subscription.trialEndsAt).toLocaleDateString()}
                  </p>
                  <p style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                    {t('subscription.salons')}: {subscription.subscription.salonCount || 0}/{subscription.subscription.max_salons} • {t('subscription.users')}: {subscription.subscription.currentUsers || 0}/{subscription.subscription.max_users} • {t('subscription.storage')}: {subscription.subscription.storageUsedGB || 0} GB / {subscription.subscription.max_storage_gb} GB
                  </p>
                </div>
                <button 
                  className="banner-button"
                  onClick={() => navigate('/pricing')}
                >
                  {t('subscription.viewPlans')}
                </button>
              </div>
            )}
            
            {!subscription.isOnTrial && subscription.subscription && !subscription.subscription.isExpired && (
              <div className="subscription-banner active">
                <div className="banner-icon">✨</div>
                <div className="banner-content">
                  <h4>{subscription.subscription.plan_display_name || subscription.subscription.plan_name} {t('subscription.plan')}</h4>
                  <p>
                    {t('subscription.activePlan')} • {t('subscription.salons')}: {subscription.subscription.salonCount || 0}/{subscription.subscription.max_salons} • {t('subscription.users')}: {subscription.subscription.currentUsers || 0}/{subscription.subscription.max_users} • {t('subscription.storage')}: {subscription.subscription.storageUsedGB || 0} GB / {subscription.subscription.max_storage_gb} GB
                  </p>
                  {subscription.subscription.current_period_end && (
                    <p style={{ marginTop: '8px', fontSize: '0.9rem' }}>
                      {t('subscription.renewsOn')} {new Date(subscription.subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <button 
                  className="banner-button"
                  onClick={() => navigate('/pricing')}
                >
                  {t('subscription.managePlan')}
                </button>
              </div>
            )}
            
            {subscription.subscription?.isExpired && (
              <div className="subscription-banner expired">
                <div className="banner-icon">⏰</div>
                <div className="banner-content">
                  <h4>{t('subscription.subscriptionExpired')}</h4>
                  <p>{t('subscription.subscriptionExpiredMessage')}</p>
                </div>
                <button 
                  className="banner-button primary"
                  onClick={() => navigate('/pricing')}
                >
                  {t('subscription.renewNow')}
                </button>
              </div>
            )}
          </>
        )}

        {/* Salon List Section */}
        <div className="salons-section">
          <h3>{t('dashboard.mySalons')}</h3>
          
          {loadingSalons && (
            <div className="loading-message">
              <div className="loading-spinner"></div>
              {t('dashboard.loading')}
            </div>
          )}

          {salonError && (
            <div className="error-message">
              {salonError}
            </div>
          )}

          {!loadingSalons && salons.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">🏢</div>
              <h4>{t('dashboard.noSalons')}</h4>
              <p>{t('dashboard.createFirst')}</p>
            </div>
          )}

          {!loadingSalons && salons.length > 0 && (
            <div className="salons-table-container">
              <table className="salons-table">
                <thead>
                  <tr>
                    <th>{t('dashboard.salonName')}</th>
                    <th>{t('common.actions')}</th>
                    <th>{t('common.description')}</th>
                    <th>{t('dashboard.createdBy')}</th>
                    <th>{t('dashboard.created')}</th>
                    <th>{t('dashboard.yourRole')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {salons.map((salon) => (
                    <tr key={salon.id} className="salon-row">
                      <td className="salon-name">
                        <strong>{salon.name}</strong>
                      </td>
                      <td className="salon-actions">
                        <button 
                          className="table-action-button primary"
                          onClick={() => navigate(`/salon/${salon.id}`)}
                        >
                          {t('salon.access')}
                        </button>
                      </td>
                      <td className="salon-description">
                        {salon.description || <span className="no-description">No description</span>}
                      </td>
                      <td className="salon-creator">
                        {salon.first_name} {salon.last_name}
                      </td>
                      <td className="salon-date">
                        {new Date(salon.created_at).toLocaleDateString()}
                      </td>
                      <td className="salon-role">
                        <span className={`role-badge role-${salon.role}`}>
                          {t(`salon.${salon.role}`)}
                        </span>
                      </td>
                      <td className="salon-delete-action">
                        {salon.role === 'owner' && (
                          <button 
                            className="delete-button"
                            onClick={() => handleDeleteSalon(salon)}
                            title={t('common.delete')}
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="features-section">
          <h3>{t('dashboard.salonManagement')}</h3>
          <div className="features-grid">
            <div className="feature-card">
              <h4>🏢 {t('salon.createSalon')}</h4>
              <p>{t('dashboard.createSalonDesc')}</p>
              <button 
                className="feature-button primary"
                onClick={handleCreateSalon}
              >
                {t('dashboard.createNewSalon')}
              </button>
            </div>
          </div>
        </div>
      </main>

      <DeleteSalonModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        salon={salonToDelete}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default Dashboard;
